// One bounded queue per authenticated owner. It never retries by itself: a later
// socket/visibility/poll signal asks again, subject to the failure cooldown.
export function createBackgroundReadQueue({ intervalMs = 15000, now = Date.now, random = Math.random,
  visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
} = {}) {
  type Entry = { nextAt: number; failures: number; pending?: Promise<any>; timer?: ReturnType<typeof setTimeout>; cancel?: () => void };
  const entries = new Map<string, Entry>();
  let active = true;
  const aborted = () => Object.assign(new Error('Background read paused'), { name: 'AbortError' });
  const run = <T,>(key: string, request: () => Promise<T>): Promise<T> => {
    if (!active || !visible()) return Promise.reject(aborted());
    const entry = entries.get(key) || { nextAt: 0, failures: 0 };
    entries.set(key, entry);
    if (entry.pending) return entry.pending;
    if (entry.failures && now() < entry.nextAt) return Promise.reject(aborted());
    const operation = new Promise<T>((resolve, reject) => {
      entry.cancel = () => reject(aborted());
      const execute = async () => {
        entry.timer = undefined;
        if (!active || !visible()) { reject(aborted()); return; }
        entry.nextAt = now() + intervalMs;
        try {
          const result = await request();
          if (!active) { reject(aborted()); return; }
          entry.failures = 0;
          resolve(result);
        } catch (error) {
          entry.failures += 1;
          const delay = Math.min(300000, 15000 * 2 ** Math.min(entry.failures - 1, 5));
          entry.nextAt = now() + Math.min(300000, delay * (1 + random() * 0.25));
          reject(error);
        }
      };
      const delay = Math.max(0, entry.nextAt - now());
      if (delay) entry.timer = setTimeout(execute, delay);
      else void execute();
    });
    const pending = operation.finally(() => {
      if (entry.pending === pending) { entry.pending = undefined; entry.cancel = undefined; }
    });
    entry.pending = pending;
    return pending;
  };
  const pause = () => {
    entries.forEach((entry) => {
      if (entry.timer) {
        clearTimeout(entry.timer); entry.timer = undefined;
        entry.cancel?.(); entry.cancel = undefined; entry.pending = undefined;
      }
    });
  };
  return { run, pause, dispose: () => { active = false; pause(); entries.forEach((entry) => entry.cancel?.()); entries.clear(); } };
}
